import { useTheme } from 'foundations-theming/utils';
import styled from 'foundations-theming/utils';
import produce from 'immer';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITextInput from 'UIComponents/input/UITextInput';
import UISelect from 'UIComponents/input/UISelect';
import UIIconButton from 'UIComponents/button/UIIconButton';
import UILink from 'UIComponents/link/UILink';
import UITile from 'UIComponents/tile/UITile';
import UITileSection from 'UIComponents/tile/UITileSection';
import { toLowerCaseAlphanumeric } from 'automation-ui-enrollment/utils/webhookUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const StyledUITileSection = styled(UITileSection).withConfig({
  displayName: "WebhookPropertyMappingTable__StyledUITileSection",
  componentId: "sc-1qyyl68-0"
})(["background-color:", ";"], ({
  theme
}) => theme.color['do-not-use-gypsum']);
const PropertyFormWrapper = ({
  children
}) => {
  return /*#__PURE__*/_jsx(UITile, {
    children: /*#__PURE__*/_jsx(StyledUITileSection, {
      className: "p-all-4",
      children: children
    })
  });
};
const getMappingError = (curMapping, allMappings) => {
  const updatedMapping = {
    webhookMapping: curMapping
  };
  const sharedName = allMappings.find(({
    webhookMapping
  }) =>
  // Check to ensure that the alphanumeric lowercase name is not the same
  toLowerCaseAlphanumeric(webhookMapping.name) === toLowerCaseAlphanumeric(curMapping.name));
  // Passes this if its the first occurrence of a duplicate, since curMapping and sameName would be the same object
  if (sharedName && sharedName.webhookMapping !== curMapping && curMapping.property !== '') {
    updatedMapping.nameError = 'DUPLICATE';
  }
  const nonEmptyPropertyButMissingName = curMapping.property !== '' && curMapping.name === '';
  if (nonEmptyPropertyButMissingName) {
    updatedMapping.nameError = 'EMPTY';
  }
  const nonEmptyNameButMissingProperty = curMapping.name !== '' && curMapping.property === '';
  if (nonEmptyNameButMissingProperty) {
    updatedMapping.propertyError = 'EMPTY';
  }
  const emptyNameAndEmptyProperty = curMapping.name === '' && curMapping.property === '' && allMappings.length !== 1;
  if (emptyNameAndEmptyProperty) {
    updatedMapping.propertyError = undefined;
    updatedMapping.nameError = undefined;
  }
  const propertyErrorIfOnlyOneMapping = curMapping.property === '' && allMappings.length === 1;
  if (propertyErrorIfOnlyOneMapping) {
    updatedMapping.propertyError = 'EMPTY';
  }
  const noPropertyType = curMapping.type === null;
  if (noPropertyType) {
    updatedMapping.typeError = 'EMPTY';
  }
  if (allMappings.length === 1 && allMappings[0].webhookMapping.property === '') {
    updatedMapping.propertyError = 'EMPTY';
  }
  if (curMapping.type === 'enumeration' && curMapping.options.length === 0) {
    updatedMapping.enumError = 'EMPTY';
  }
  return updatedMapping;
};
const getDataTypeDropdownOptions = () => [{
  value: 'string',
  text: I18n.text('automation-ui-enrollment.webhookWizard.setupEvent.properties.dataTypeDropDownOptions.string')
}, {
  value: 'number',
  text: I18n.text('automation-ui-enrollment.webhookWizard.setupEvent.properties.dataTypeDropDownOptions.number')
}, {
  value: 'bool',
  text: I18n.text('automation-ui-enrollment.webhookWizard.setupEvent.properties.dataTypeDropDownOptions.boolean')
}, {
  value: 'enumeration',
  text: I18n.text('automation-ui-enrollment.webhookWizard.setupEvent.properties.dataTypeDropDownOptions.enum')
}];
const WebhookPropertyMappingTable = ({
  mappings,
  setMappings,
  dropDownOptions,
  hasTooManyMappings
}) => {
  const trellisTheme = useTheme();
  const DATA_TYPE_DROPDOWN_OPTIONS = getDataTypeDropdownOptions();
  const handleUpdatePropertyMapping = (newMapping, index) => {
    const newMappings = [...mappings];
    newMappings[index] = newMapping;
    setMappings(newMappings.map(({
      webhookMapping
    }) => getMappingError(webhookMapping, newMappings)));
  };
  const handleDelete = mappingToDelete => {
    const newMappings = mappings.filter(mapping => mapping !== mappingToDelete);
    setMappings(newMappings.map(({
      webhookMapping
    }) => getMappingError(webhookMapping, newMappings)));
  };
  const handleUpdateDataType = (newType, curMapping, index) => {
    let newWebhookMapping;
    if (newType === 'enumeration') {
      newWebhookMapping = Object.assign({}, curMapping.webhookMapping, {
        type: newType,
        options: []
      });
    } else {
      newWebhookMapping = Object.assign({}, curMapping.webhookMapping, {
        type: newType
      });
    }
    const newMapping = {
      webhookMapping: newWebhookMapping
    };
    const newMappings = [...mappings];
    newMappings[index] = newMapping;
    setMappings(newMappings.map(({
      webhookMapping
    }) => getMappingError(webhookMapping, newMappings)));
  };
  const handleUpdateEnumValues = (newValues, curMapping, index) => {
    const newMapping = produce(curMapping, draftMapping => {
      draftMapping.options = newValues.map(value => ({
        label: value,
        value: toLowerCaseAlphanumeric(value)
      }));
      return draftMapping;
    });
    const newMappings = [...mappings];
    newMappings[index] = {
      webhookMapping: newMapping
    };
    setMappings(newMappings.map(({
      webhookMapping
    }) => getMappingError(webhookMapping, newMappings)));
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [mappings.map((mapping, index) => /*#__PURE__*/_jsxs(UIGrid, {
      className: "m-bottom-4",
      children: [/*#__PURE__*/_jsx(UIGridItem, {
        size: 11,
        children: /*#__PURE__*/_jsxs(PropertyFormWrapper, {
          children: [/*#__PURE__*/_jsx(UIFormControl, {
            required: true,
            label: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "automation-ui-enrollment.webhookWizard.setupEvent.properties.label"
            }),
            tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "automation-ui-enrollment.webhookWizard.setupEvent.properties.labelHoverInfo"
            }),
            validationMessage: mapping.propertyError && /*#__PURE__*/_jsx(FormattedMessage, {
              message: "automation-ui-enrollment.webhookWizard.setupEvent.properties.errors.empty"
            }),
            error: !!mapping.propertyError,
            children: /*#__PURE__*/_jsx(UISelect, {
              buttonUse: "form-on-dark",
              options: dropDownOptions,
              placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "automation-ui-enrollment.webhookWizard.setupEvent.properties.eventPropertyPlaceholder"
              }),
              onChange: event => {
                handleUpdatePropertyMapping(Object.assign({}, mapping, {
                  webhookMapping: Object.assign({}, mapping.webhookMapping, {
                    property: event.target.value
                  })
                }), index);
              },
              value: mapping.webhookMapping.property
            })
          }), mapping.webhookMapping.property && /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsxs(UIGrid, {
              children: [/*#__PURE__*/_jsx(UIGridItem, {
                size: 7,
                children: /*#__PURE__*/_jsx(UIFormControl, {
                  verticalSeparation: "flush",
                  required: true,
                  tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "automation-ui-enrollment.webhookWizard.setupEvent.properties.fieldNameHoverInfo"
                  }),
                  label: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "automation-ui-enrollment.webhookWizard.setupEvent.properties.fieldName"
                  }),
                  error: !!mapping.nameError,
                  validationMessage: mapping.nameError && /*#__PURE__*/_jsx(FormattedMessage, {
                    message: mapping.nameError === 'DUPLICATE' ? 'automation-ui-enrollment.webhookWizard.setupEvent.properties.errors.duplicate' : 'automation-ui-enrollment.webhookWizard.setupEvent.properties.errors.empty'
                  }),
                  children: /*#__PURE__*/_jsx(UITextInput, {
                    use: "on-dark",
                    placeholder: I18n.text('automation-ui-enrollment.webhookWizard.setupEvent.properties.eventPropertyLabelPlaceholder'),
                    value: mapping.webhookMapping.name,
                    onChange: event => handleUpdatePropertyMapping(Object.assign({}, mapping, {
                      webhookMapping: Object.assign({}, mapping.webhookMapping, {
                        name: event.target.value
                      })
                    }), index)
                  })
                })
              }), /*#__PURE__*/_jsx(UIGridItem, {
                size: 5,
                children: /*#__PURE__*/_jsx(UIFormControl, {
                  required: true,
                  verticalSeparation: "flush",
                  label: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "automation-ui-enrollment.webhookWizard.setupEvent.properties.dataTypeLabel"
                  }),
                  children: /*#__PURE__*/_jsx(UISelect, {
                    buttonUse: "form-on-dark",
                    options: DATA_TYPE_DROPDOWN_OPTIONS,
                    value: mapping.webhookMapping.type,
                    placeholder: "",
                    onChange: evt => handleUpdateDataType(evt.target.value, mapping, index)
                  })
                })
              })]
            }), mapping.webhookMapping.type === 'enumeration' && /*#__PURE__*/_jsx(UIFormControl, {
              required: true,
              verticalSeparation: "flush",
              label: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "automation-ui-enrollment.webhookWizard.setupEvent.properties.enum.mainLabel"
              }),
              help: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "automation-ui-enrollment.webhookWizard.setupEvent.properties.enum.helperLabel"
              }),
              error: !!mapping.enumError,
              validationMessage: mapping.enumError && /*#__PURE__*/_jsx(FormattedMessage, {
                message: "automation-ui-enrollment.webhookWizard.setupEvent.properties.enum.emptyError"
              }),
              children: /*#__PURE__*/_jsx(UISelect, {
                className: "testing-id-enum-dropdown",
                style: {
                  backgroundColor: trellisTheme.color['do-not-use-olaf']
                },
                multi: true,
                allowCreate: true,
                placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "automation-ui-enrollment.webhookWizard.setupEvent.properties.enum.placeholder"
                }),
                onChange: evt => handleUpdateEnumValues(evt.target.value, mapping.webhookMapping, index),
                defaultValue: mapping.webhookMapping.options.map(({
                  label
                }) => label),
                promptTextCreator: label => /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "automation-ui-enrollment.webhookWizard.setupEvent.properties.enum.addOption",
                  options: {
                    label
                  }
                })
              })
            })]
          })]
        })
      }), /*#__PURE__*/_jsx(UIGridItem, {
        size: 1,
        className: "m-y-auto",
        children: /*#__PURE__*/_jsx(UIIconButton, {
          disabled: mappings.length === 1,
          tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation-ui-enrollment.webhookWizard.setupEvent.properties.deleteToolTip"
          }),
          use: "transparent",
          placement: "bottom",
          onClick: () => handleDelete(mapping),
          children: /*#__PURE__*/_jsx(UIIcon, {
            name: "delete"
          })
        })
      })]
    }, `${mapping.webhookMapping.property}-${index}`)), mappings.length < dropDownOptions.flatMap(dropdown => 'options' in dropdown ? dropdown.options : dropdown).length && /*#__PURE__*/_jsx(UILink, {
      disabled: hasTooManyMappings,
      onClick: () => setMappings([...mappings, {
        webhookMapping: {
          name: '',
          property: '',
          type: 'string'
        }
      }]),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-enrollment.webhookWizard.setupEvent.properties.addProperty"
      })
    })]
  });
};
export default WebhookPropertyMappingTable;